import classNames from 'classnames';

import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { NewScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';

import { MyCardsRaritySwitcherBox } from './ui';

type Props = {
  rarityPath: (rarity: Rarity) => string;
  type: Rarity;
  onClick?: (rarity: Rarity) => void;
};

export const CardOrCollectionRaritySwitcher = ({
  rarityPath,
  type,
  onClick,
}: Props) => {
  const { currentUser } = useCurrentUserContext();
  const { cardCounts } = currentUser || {};
  const {
    flags: { useMlbSealing = false },
  } = useFeatureFlags();
  const { sport } = useSportContext();

  const cardsCountByRarity = {
    [Rarity.common]: cardCounts?.common || 0,
    [Rarity.limited]: cardCounts?.limited || 0,
    [Rarity.rare]: cardCounts?.rare || 0,
    [Rarity.super_rare]: cardCounts?.superRare || 0,
    [Rarity.unique]: cardCounts?.unique || 0,
  };

  const isVaultEnabled = useMlbSealing && sport === Sport.BASEBALL;
  const showLimitedByDefault =
    isVaultEnabled &&
    !cardCounts?.rare &&
    !cardCounts?.superRare &&
    !cardCounts?.unique;

  const displayedRarities = Object.entries(cardsCountByRarity).filter(
    ([, value], index) => !!value || (showLimitedByDefault && index === 1)
  );

  return (
    <Horizontal>
      {displayedRarities.map(([key, value]) => (
        <MyCardsRaritySwitcherBox
          key={key}
          to={rarityPath(key as keyof typeof cardsCountByRarity)}
          className={classNames({
            active: type === key,
          })}
          onClick={
            onClick
              ? () => {
                  onClick(key as keyof typeof cardsCountByRarity);
                }
              : undefined
          }
        >
          <NewScarcityIcon
            rarity={key as keyof typeof cardsCountByRarity}
            size={5}
          />
          <LabelM bold>{value}</LabelM>
        </MyCardsRaritySwitcherBox>
      ))}
    </Horizontal>
  );
};
